import React from 'react';

import { Card, Row, Col, Button } from 'react-bootstrap';
import { FaEye, FaArrowRight, FaStar, FaHistory } from 'react-icons/fa';

import { Link } from 'react-router-dom';

import { useProductRatings } from '../../hooks/useProductRatings';

/*
  Reusable grid of compact product cards used by the "Suggested For You",
  "Recommended Products" and "Recently Viewed" sections on the product
  details page. Renders nothing when there are no products.

  @param title       section heading (may include an emoji)
  @param subtitle    optional supporting line under the heading
  @param badge       optional right-side badge text (e.g. "3 items")
  @param products    array of products to show
  @param onQuickView called with a product when its Quick View is clicked
  @param variant     'default' (saffron) or 'recent' — the plum-cast browsing
                     history treatment, which expects `products` ordered
                     most-recently-viewed first
*/
function ProductMiniGrid({
  title,
  subtitle,
  badge,
  products,
  onQuickView,
  variant = 'default'
}) {
  const { getRating } = useProductRatings();

  if (!products || products.length === 0) {
    return null;
  }

  const isRecent = variant === 'recent';

  return (
    <Card className={`border-0 pmg-section mt-4${isRecent ? ' pmg-recent' : ''}`}>
      <Card.Body className="p-3 p-md-4">
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="fw-bold mb-0 d-flex align-items-center gap-2 pmg-title">
              <span className="pmg-title-bar" /> {title}
            </h3>
            {subtitle && <p className="pmg-subtitle mb-0">{subtitle}</p>}
          </div>
          {badge && <span className="pmg-count">{badge}</span>}
        </div>

        <Row className="g-3 g-md-4">
          {products.map((item, i) => {
            const recOld = Math.round(item.price * 1.2);
            const recOff = Math.round(((recOld - item.price) / recOld) * 100);
            const save = recOld - item.price;

            return (
              <Col xl={3} lg={3} md={4} sm={6} xs={6} key={item._id}>
                <Card
                  className={`border-0 h-100 pmg-card${
                    isRecent && i === 0 ? ' is-latest' : ''
                  }`}
                  style={{ animationDelay: `${i * 70}ms` }}
                >
                  <div className="pmg-img-wrap">
                    {recOff > 0 && (
                      <span className="pmg-badge">{recOff}% OFF</span>
                    )}

                    {isRecent && (
                      <span className="pmg-seen">
                        {i === 0 ? (
                          <>
                            <span className="pmg-seen-dot" /> Last viewed
                          </>
                        ) : (
                          `#${i + 1}`
                        )}
                      </span>
                    )}

                    <img
                      src={item.image}
                      alt={item.name}
                      className="pmg-img"
                    />

                    {/* Light sweep on hover — sits above the image but below
                        the overlay, which follows it in source order. */}
                    {isRecent && <span className="pmg-shine" />}

                    <button
                      type="button"
                      className="pmg-overlay"
                      onClick={() => onQuickView(item)}
                    >
                      <span className="pmg-overlay-btn">
                        <FaEye className="me-1" /> Quick View
                      </span>
                    </button>
                  </div>

                  <Card.Body className="d-flex flex-column p-3">
                    <div className="pmg-stars mb-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <FaStar
                          key={s}
                          className={
                            s <= Math.round(getRating(item._id).averageRating)
                              ? ''
                              : 'pmg-star-off'
                          }
                        />
                      ))}

                      <span className="pmg-rating">
                        {getRating(item._id).totalReviews > 0
                          ? `${getRating(item._id).averageRating.toFixed(1)} (${
                              getRating(item._id).totalReviews
                            })`
                          : 'No reviews yet'}
                      </span>
                    </div>

                    <h6 className="fw-bold pmg-name mb-2">{item.name}</h6>

                    <div className="d-flex align-items-baseline flex-wrap gap-2 mb-1">
                      <span className="pmg-price">₹{item.price}</span>
                      <small className="pmg-old">₹{recOld}</small>
                    </div>

                    {save > 0 && (
                      <div className="pmg-save mb-3">You save ₹{save}</div>
                    )}

                    <Button
                      as={Link}
                      to={`/product/${item._id}`}
                      className="mt-auto w-100 pmg-btn"
                    >
                      {isRecent && <FaHistory className="me-2" />}
                      {isRecent ? 'View Again' : 'View Details'}{' '}
                      <FaArrowRight className="pmg-btn-arrow ms-1" />
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card.Body>

      <style>{pmgStyles}</style>
    </Card>
  );
}

const pmgStyles = `
  /* ---- Divine palette -------------------------------------------------
     One token set drives both shelves: temple gold and saffron over deep
     maroon, laid on ivory. The "recent" variant only re-points these tokens
     to the antique end of the same range, so nothing can drift out of the
     palette and the two shelves still read apart. */
  .pmg-section {
    --dv-ivory:   #fffdf7;
    --dv-cream:   #fff6e3;
    --dv-gold-lt: #fde68a;
    --dv-gold:    #f0b429;
    --dv-gold-dp: #d97706;
    --dv-saffron: #ea580c;
    --dv-maroon:  #7c2d12;
    --dv-ink:     #4a2410;
    --dv-muted:   #a98466;
    --dv-glow:    240,180,41;
    --dv-cast:    124,45,18;

    border-radius: 26px;
    position: relative;
    overflow: hidden;
    background:
      radial-gradient(660px 230px at 100% 0%, rgba(var(--dv-glow),0.16), transparent 70%),
      radial-gradient(520px 220px at 0% 100%, rgba(234,88,12,0.09), transparent 72%),
      linear-gradient(180deg, var(--dv-ivory), var(--dv-cream));
    box-shadow: 0 18px 50px rgba(var(--dv-cast),0.12);
  }
  .pmg-section::before {
    content: '';
    position: absolute;
    z-index: 2;
    top: 0; left: 0; right: 0;
    height: 5px;
    background: linear-gradient(90deg,
      var(--dv-maroon), var(--dv-saffron) 38%, var(--dv-gold) 68%, var(--dv-gold-lt));
  }
  /* A halo cresting over the top edge — the section's own aura. Clipped by
     the card's overflow, so only the lower arc shows. */
  .pmg-section::after {
    content: '';
    position: absolute;
    z-index: 0;
    top: -110px; left: 50%;
    width: 460px; height: 200px;
    transform: translateX(-50%);
    pointer-events: none;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(var(--dv-glow),0.32), transparent 70%);
    animation: pmgHalo 7s ease-in-out infinite;
  }
  @keyframes pmgHalo {
    0%, 100% { opacity: 0.55; transform: translateX(-50%) scale(0.94); }
    50%      { opacity: 1;    transform: translateX(-50%) scale(1.08); }
  }
  /* Keeps the grid above the halo, which is positioned and would otherwise
     paint over the unpositioned body. */
  .pmg-section > .card-body {
    position: relative;
    z-index: 1;
  }

  .pmg-title {
    font-size: clamp(1.3rem, 3vw, 1.7rem);
    color: var(--dv-maroon);
  }
  .pmg-title-bar {
    display: inline-block;
    width: 6px; height: 28px;
    border-radius: 6px;
    background: linear-gradient(180deg, var(--dv-saffron), var(--dv-gold));
    box-shadow: 0 0 10px rgba(var(--dv-glow),0.55);
  }
  .pmg-subtitle {
    margin-top: 7px;
    padding-left: 16px;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--dv-muted);
  }
  .pmg-count {
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--dv-gold-dp);
    background: #fffaf0;
    border: 1px solid var(--dv-gold-lt);
    padding: 7px 16px;
    border-radius: 30px;
    box-shadow: 0 4px 10px rgba(var(--dv-glow),0.22);
    white-space: nowrap;
  }

  .pmg-card {
    border-radius: 20px;
    overflow: hidden;
    background: #fffefb;
    border: 1px solid #f2e4c9 !important;
    transition: transform 0.32s ease, box-shadow 0.32s ease, border-color 0.32s ease;
    animation: pmgUp 0.5s ease both;
  }
  .pmg-card:hover {
    transform: translateY(-8px);
    border-color: rgba(var(--dv-glow),0.6) !important;
    box-shadow: 0 24px 44px rgba(var(--dv-cast),0.22) !important;
  }
  @keyframes pmgUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .pmg-img-wrap { position: relative; overflow: hidden; }
  .pmg-img {
    width: 100%;
    height: clamp(150px, 20vw, 205px);
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  .pmg-card:hover .pmg-img { transform: scale(1.1); }

  /* Kept red, but shifted to a sacred vermillion so the discount still reads
     as a discount without falling outside the palette. */
  .pmg-badge {
    position: absolute;
    top: 10px; left: 10px;
    z-index: 2;
    background: linear-gradient(135deg, #dc2626, #86160f);
    color: #fff6e3;
    font-size: 0.72rem;
    font-weight: 800;
    padding: 5px 12px;
    border-radius: 30px;
    box-shadow: 0 4px 12px rgba(134,22,15,0.45);
  }

  .pmg-overlay {
    position: absolute;
    inset: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    background: linear-gradient(to top, rgba(74,20,8,0.62), rgba(74,20,8,0.04));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .pmg-card:hover .pmg-overlay { opacity: 1; }
  .pmg-overlay-btn {
    background: rgba(255,253,247,0.97);
    color: var(--dv-maroon);
    font-weight: 700;
    font-size: 0.82rem;
    padding: 9px 18px;
    border-radius: 50px;
    transform: translateY(12px);
    transition: transform 0.3s ease, background 0.2s ease;
    box-shadow: 0 8px 18px rgba(var(--dv-cast),0.35);
  }
  .pmg-card:hover .pmg-overlay-btn { transform: translateY(0); }
  .pmg-overlay-btn:hover { background: var(--dv-gold-lt); }

  .pmg-stars {
    color: var(--dv-gold);
    font-size: 0.72rem;
    display: flex;
    align-items: center;
    gap: 1px;
  }
  .pmg-star-off {
    color: #e6d9bd;
  }
  .pmg-rating {
    color: var(--dv-muted);
    font-weight: 600;
    margin-left: 5px;
    font-size: 0.72rem;
  }
  .pmg-name {
    font-size: 0.95rem;
    color: var(--dv-ink);
    min-height: 1.2em;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  /* Price and savings move off the conventional green onto temple maroon and
     gold — the whole shelf is one palette now, and maroon on ivory still
     carries more contrast than the green did. */
  .pmg-price {
    font-size: 1.35rem;
    font-weight: 800;
    color: var(--dv-maroon);
  }
  .pmg-old { color: var(--dv-muted); text-decoration: line-through; }
  .pmg-save {
    align-self: flex-start;
    font-size: 0.7rem;
    font-weight: 700;
    color: var(--dv-maroon);
    background: #fef3c7;
    border: 1px solid var(--dv-gold-lt);
    padding: 2px 10px;
    border-radius: 20px;
  }

  /* ---- The divine CTA -------------------------------------------------
     Molten temple gold under deep maroon lettering, ringed by a gold halo
     on hover, with a gleam that crosses the face. Background is restated on
     hover so Bootstrap's own .btn:hover background-color can never surface. */
  .pmg-btn {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(var(--dv-cast),0.18) !important;
    border-radius: 12px;
    font-weight: 800;
    letter-spacing: 0.3px;
    padding: 11px 10px;
    color: #4a1503 !important;
    background: linear-gradient(135deg,
      #fef6d8 0%, var(--dv-gold-lt) 22%, var(--dv-gold) 52%, var(--dv-gold-dp) 100%);
    box-shadow:
      0 6px 16px rgba(var(--dv-glow),0.38),
      inset 0 1px 0 rgba(255,255,255,0.7);
    transition: transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease;
  }
  .pmg-btn:hover,
  .pmg-btn:focus-visible {
    color: #3d1102 !important;
    transform: translateY(-3px);
    background: linear-gradient(135deg,
      #fffbe8 0%, var(--dv-gold-lt) 20%, var(--dv-gold) 50%, var(--dv-gold-dp) 100%);
    box-shadow:
      0 14px 30px rgba(var(--dv-glow),0.55),
      0 0 0 3px rgba(var(--dv-glow),0.3),
      inset 0 1px 0 rgba(255,255,255,0.85);
    filter: saturate(1.06);
  }
  .pmg-btn::before {
    content: '';
    position: absolute;
    top: 0; bottom: 0; left: -45%;
    width: 36%;
    pointer-events: none;
    transform: skewX(-20deg);
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent);
    opacity: 0;
  }
  .pmg-btn:hover::before {
    opacity: 1;
    animation: pmgGleam 0.8s ease-out;
  }
  @keyframes pmgGleam {
    from { left: -45%; }
    to   { left: 115%; }
  }
  .pmg-btn-arrow { transition: transform 0.25s ease; }
  .pmg-btn:hover .pmg-btn-arrow { transform: translateX(4px); }

  /* ---- "Recently Viewed" variant -------------------------------------
     Same divine palette, re-pointed to its antique end: aged brass instead
     of bright gold, over a darker sanctum maroon. That alone separates this
     shelf from the "Suggested For You" grid above it, because every rule
     below reads the tokens. Declared after the base block so the shared
     single-class selectors resolve here. */
  .pmg-recent {
    --dv-ivory:   #fffaf0;
    --dv-cream:   #fdeed4;
    --dv-gold-lt: #f8d77e;
    --dv-gold:    #dfa218;
    --dv-gold-dp: #b45309;
    --dv-saffron: #c2410c;
    --dv-maroon:  #5f1f0a;
    --dv-ink:     #3f1d0c;
    --dv-muted:   #9c7550;
    --dv-glow:    223,162,24;
    --dv-cast:    95,31,10;
  }
  .pmg-recent .pmg-card { background: #fffcf5; }

  /* Order marker. The freshest card gets the full pill with a live dot; the
     rest carry just their position, so the row reads as a trail without
     four competing badges. */
  .pmg-seen {
    position: absolute;
    top: 10px; right: 10px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.3px;
    color: #fff6e3;
    background: linear-gradient(135deg, var(--dv-gold-dp), var(--dv-maroon));
    padding: 5px 11px;
    border-radius: 30px;
    box-shadow: 0 4px 12px rgba(var(--dv-cast),0.45);
    pointer-events: none;
  }
  /* Two-up cards on phones leave little room between this and the % OFF
     badge in the opposite corner, so both tighten up. */
  @media (max-width: 575px) {
    .pmg-seen { top: 8px; right: 8px; font-size: 0.6rem; padding: 4px 8px; }
    .pmg-recent .pmg-badge { font-size: 0.62rem; padding: 4px 8px; }
  }
  .pmg-seen-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--dv-gold-lt);
    animation: pmgSeen 1.9s ease-in-out infinite;
  }
  @keyframes pmgSeen {
    0%, 100% { box-shadow: 0 0 0 0 rgba(248,215,126,0.9); opacity: 1; }
    50%      { box-shadow: 0 0 0 6px rgba(248,215,126,0);  opacity: 0.6; }
  }

  /* z-index 0 keeps the sweep above the (unpositioned) image while the
     overlay, later in source order, still paints over it. */
  .pmg-shine {
    position: absolute;
    z-index: 0;
    top: 0; bottom: 0;
    left: -60%;
    width: 42%;
    pointer-events: none;
    transform: skewX(-18deg);
    background: linear-gradient(90deg, transparent, rgba(255,246,222,0.6), transparent);
    opacity: 0;
  }
  .pmg-recent .pmg-card:hover .pmg-shine {
    opacity: 1;
    animation: pmgShine 0.9s ease-out;
  }
  @keyframes pmgShine {
    from { left: -60%; }
    to   { left: 120%; }
  }

  /* The most recent card keeps a standing gold ring, so the eye lands on it
     before any hovering happens. */
  .pmg-recent .pmg-card.is-latest {
    border-color: rgba(var(--dv-glow),0.65) !important;
    box-shadow: 0 12px 28px rgba(var(--dv-glow),0.28) !important;
  }
  /* Needed because the standing ring above outranks the base .pmg-card:hover,
     which would otherwise leave this one card inert on hover. */
  .pmg-recent .pmg-card.is-latest:hover {
    border-color: rgba(var(--dv-glow),0.9) !important;
    box-shadow: 0 24px 46px rgba(var(--dv-glow),0.42) !important;
  }
  .pmg-recent .pmg-card { animation-name: pmgTrail; }
  @keyframes pmgTrail {
    from { opacity: 0; transform: translateX(-20px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  @media (prefers-reduced-motion: reduce) {
    .pmg-card,
    .pmg-section::after { animation: none; }
    .pmg-seen-dot,
    .pmg-recent .pmg-card:hover .pmg-shine,
    .pmg-btn:hover::before { animation: none; }
    .pmg-shine,
    .pmg-btn::before { display: none; }
  }
`;

export default ProductMiniGrid;
